import { Table } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { QueueMetrics } from 'lib-dashboard-types';
import { SearchQueue } from 'lib-search-types';
import noop from 'lodash/noop';
import {
  forwardRef,
  HTMLAttributes,
  Ref,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DragMoveEvent, useOrderedDragDrop } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardCard } from '../../../../../../components/card';
import { QueueDashboardMetrics } from '../metrics/queueDashboardMetrics';

import { QueueDashboardQueueActions } from './components/actions/queueDashboardQueueActions';
import { QueueDashboardQueueDragHandle } from './components/dragHandle/queueDashboardQueueDragHandle';
import { QueueDashboardQueueTitle } from './components/title/queueDashboardQueueTitle';
import {
  QueueDashboardQueueContext,
  QueueDashboardQueueContextValue,
} from './queueDashboardQueue.context';
import styles from './queueDashboardQueue.module.scss';

const type = Symbol('queueDashboardQueue');

type QueueDashboardQueueProps = {
  /** The queue. */
  queue: SearchQueue;
  /** The index of the queue. */
  index: number;
  /** The total number of queues. */
  totalQueues: number;
  /** Whether to show the skeleton UI, e.g. when loading. */
  isSkeleton?: boolean;
  /** The metrics data for the current dashboard queue. */
  queueMetrics?: QueueMetrics;
  /** Handler called when the order of the queues changes. */
  onOrderChange?: (event: DragMoveEvent) => void;
};

export const QueueDashboardQueue = forwardRef<
  HTMLDivElement | HTMLTableRowElement,
  QueueDashboardQueueProps
>(function QueueDashboardQueue(
  {
    queue,
    index,
    totalQueues,
    isSkeleton = false,
    queueMetrics,
    onOrderChange,
  },
  forwardedRef,
): JSX.Element {
  const storage = useServerStorage('user');
  const { layout } = storage.data.dashboard ?? {};

  const ref = useRef<HTMLDivElement | HTMLTableRowElement>(null);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);

  useImperativeHandle(
    forwardedRef,
    () => ref.current as HTMLDivElement | HTMLTableRowElement,
  );

  const { isDragging, isOver } = useOrderedDragDrop({
    type,
    index,
    source: dragHandleRef,
    target: ref,
    preview: ref,
    onMove: onOrderChange ?? noop,
    triggerSwap: 'drop',
  });

  const orderingProps: HTMLAttributes<HTMLDivElement | HTMLTableRowElement> =
    useMemo(
      () => ({
        className: classNames(styles.root, {
          [styles.isDragging]: isDragging,
          [styles.isOver]: isOver,
        }),
        ...(onOrderChange && {
          onMouseEnter: () => setIsHovered(true),
          onMouseLeave: () => setIsHovered(false),
        }),
      }),
      [isDragging, onOrderChange, isOver],
    );

  const contextValue: QueueDashboardQueueContextValue = useMemo(
    () => ({
      queue,
      index,
      totalQueues,
      isSkeleton,
      layout,
    }),
    [index, isSkeleton, totalQueues, layout, queue],
  );

  const renderCompactLayout = (): JSX.Element => (
    <Table.Tr ref={ref as Ref<HTMLTableRowElement>} {...orderingProps}>
      <Table.Td style={{ position: 'relative' }}>
        {onOrderChange && (
          <QueueDashboardQueueDragHandle
            ref={dragHandleRef}
            isVisible={isHovered}
          />
        )}

        <QueueDashboardQueueTitle />
      </Table.Td>

      <QueueDashboardMetrics queueMetrics={queueMetrics} />

      <Table.Td>
        <QueueDashboardQueueActions onOrderChange={onOrderChange} />
      </Table.Td>
    </Table.Tr>
  );

  const renderDefaultLayout = (): JSX.Element => (
    <DashboardCard
      ref={ref as Ref<HTMLDivElement>}
      titleSlot={<QueueDashboardQueueTitle />}
      actionsSlot={<QueueDashboardQueueActions onOrderChange={onOrderChange} />}
      dragHandleSlot={
        onOrderChange && (
          <QueueDashboardQueueDragHandle
            ref={dragHandleRef}
            isVisible={isHovered}
          />
        )
      }
      {...orderingProps}
    >
      <QueueDashboardMetrics queueMetrics={queueMetrics} />
    </DashboardCard>
  );

  return (
    <QueueDashboardQueueContext.Provider value={contextValue}>
      {layout === 'compact' ? renderCompactLayout() : renderDefaultLayout()}
    </QueueDashboardQueueContext.Provider>
  );
});
